<script setup lang="ts">
import { computed } from 'vue';
import { DynamicScroller, DynamicScrollerItem } from 'vue-virtual-scroller';

import InsightsListItem from '@/components/insights/InsightsListItem.vue';
import VLink from '@/components/ui/VLink.vue';
import LoaderBlock from '@/components/_common/LoaderBlock.vue';

import { setTitle } from '@/utils/title';
import { useInsights } from '@/composables/useInsights';
import { useSearchStore } from '@/stores/searchStore';

setTitle('All Insights');

const searchStore = useSearchStore();
const { insights, isLoading, error, searchInsights, refetch } = useInsights();

const filteredInsights = computed(() => {
  const query = searchStore.normalizedQuery;
  if (!query?.trim()) return insights.value || [];

  return searchInsights(query);
});
</script>

<template>
  <div class="h-full">
    <div class="h-12 bg-gpx_gray-900"></div>

    <!-- Loading state -->
    <div
      v-if="isLoading"
      class="flex h-[calc(100%-48px)] items-center justify-center"
    >
      <LoaderBlock />
    </div>

    <!-- Error state -->
    <div
      v-else-if="error"
      class="flex h-[calc(100%-48px)] items-center justify-center"
    >
      <div class="text-center text-gpx_gray-700">
        <p class="mb-2">Failed to load insights</p>
        <button @click="refetch" class="text-blue-500 hover:underline">
          Try again
        </button>
      </div>
    </div>

    <!-- No data state -->
    <div
      v-else-if="filteredInsights.length === 0"
      class="flex h-[calc(100%-48px)] items-center justify-center"
    >
      <div class="text-center text-gpx_gray-700">
        <p>
          {{
            searchStore.normalizedQuery
              ? 'No insights match your search'
              : 'No insights available'
          }}
        </p>
      </div>
    </div>

    <!-- Data loaded state -->
    <DynamicScroller
      v-else
      class="h-[calc(100%-48px)] sm:h-[calc(100%-64px)]"
      :items="filteredInsights"
      :min-item-size="70"
      :emit-update="true"
      key-field="id"
      listClass="ds-insights-list"
      itemClass="ds-insights-list-item"
    >
      <template v-slot="{ item, index, active }">
        <DynamicScrollerItem
          :item="item"
          :active="active"
          :size-dependencies="[item.title, item.created_at]"
          :data-index="index"
        >
          <div class="border-b border-gpx_gray-900">
            <VLink
              block
              :to="`/insights/${item.id}`"
              class="bg-white hover:bg-gpx_gray-900"
            >
              <div class="flex py-[15px] pl-6 pr-[48px] md:pl-12 md:pr-[56px]">
                <InsightsListItem
                  class="w-full overflow-hidden"
                  :insight="item"
                />
              </div>
            </VLink>
          </div>
        </DynamicScrollerItem>
      </template>
    </DynamicScroller>
  </div>
</template>

<style>
.ds-insights-list {
  @apply !overflow-visible;
}
</style>
